import './styles/index.scss';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { Navbar, Badge, NavDropdown, MenuItem } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Store } from 'react-notifications-component';
import PropTypes from 'prop-types';

import Tooltip from './components/Tooltip';
import Switch from './components/Switch';
import NavItem from './components/NavItem';
import Guides from './components/Guides';
import AdminNotification from './components/Notification';
import Loader from '../Loaders/Loader';

import { getPartsOfArray, prepareRoutes } from './helpers';
import { switchViewModeUrl } from '../../services/endpoints/general';
import { getErrorMessage } from '../../utils/helper';
import { WebsocketContext } from '../../utils/WebsocketProvider';
import { authLogout } from '../../services/endpoints/auth';
import { userLogoutCredentialsAction } from '../../redux/actions/user';

const AdminNavbar = ({ component: RouterLink, accessRoutes, defaultRoutes, parsingCredits, darkMode }) => {
  const dispatch = useDispatch();

  const { onClose: onCloseWebsocket, eventData, socketsInit } = useContext(WebsocketContext);

  const mainNavbar = useRef(null);
  const rightNavbar = useRef(null);
  const navbarItemsWidth = useRef(null);
  const guidesFrameRef = useRef(null);

  const fullNavbarRoutes = prepareRoutes(defaultRoutes, accessRoutes);
  const mainRoutes = getPartsOfArray(fullNavbarRoutes, fullNavbarRoutes?.length - 2);
  const accountItem = fullNavbarRoutes?.find((item) => item.name === 'Account');

  const [openMore, setOpenMore] = useState(false);
  const [openAcc, setOpenAcc] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(!!darkMode);
  const [isLoadingDark, setIsLoadingDark] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [updateNoti, setUpdateNoti] = useState(false);
  const [navbar, setNavbar] = useState({
    priorityItems: mainRoutes,
    moreItems: [],
  });
  const [isLogoutLoading, setLogoutLoading] = useState(false);

  const onToggleMore = (open) => setOpenMore(open);
  const onToggleAcc = (open) => setOpenAcc(open);

  const computedMaxShowIndex = (array, maxWidth) => {
    let total = 0;
    for (let i = 0; i < array.length; i++) {
      if (total > maxWidth) {
        return i;
      }
      total += array[i];
    }
  };

  const updateNavigation = () => {
    if (window.innerWidth < 768) {
      return setNavbar({
        priorityItems: mainRoutes,
        moreItems: [],
      });
    }

    const computedMaxWidth = window.innerWidth - rightNavbar.current?.getBoundingClientRect()?.width - 235;
    const computedIndex = computedMaxShowIndex(navbarItemsWidth.current, computedMaxWidth);
    setNavbar({
      priorityItems: getPartsOfArray(mainRoutes, computedIndex),
      moreItems: getPartsOfArray(mainRoutes, -(mainRoutes.length - computedIndex)),
    });
  };

  useEffect(() => {
    navbarItemsWidth.current = Array.from(mainNavbar.current.children).map((item) => item.getBoundingClientRect().width);
    window.addEventListener('resize', updateNavigation);
    updateNavigation();

    return () => {
      window.removeEventListener('resize', updateNavigation);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    switch (eventData?.name) {
    case 'admin.events.new':
      setUpdateNoti(true);
      break;

    case 'menu_counter_update':
      dispatch({
        type: '@@header/GET_NAVBAR_COUNTERS_SUCCESS',
        data: [eventData?.options],
      });
      break;

    default:
      break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  useEffect(() => {
    updateNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessRoutes]);

  useEffect(() => {
    if (expanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [expanded]);

  const onChangeSwitch = async () => {
    setIsDarkMode(!isDarkMode);
    const iframeDoc = guidesFrameRef.current?.frameRef?.contentWindow
      ? guidesFrameRef.current?.frameRef?.contentWindow.document
      : guidesFrameRef.current?.frameRef?.contentDocument;
    if (isDarkMode) {
      document.body.classList.remove('dark-mode');
      iframeDoc?.body?.classList?.remove('dark-mode');
    } else {
      document.body.classList.add('dark-mode');
      iframeDoc?.body?.classList?.add('dark-mode');
    }

    setIsLoadingDark(true);
    try {
      dispatch({ type: '@@general/CHANGE_VIEW_MODE' });
      await switchViewModeUrl(Number(!isDarkMode));
      dispatch({
        type: '@@general/CHANGE_VIEW_MODE_SUCCESS',
        payload: Number(!isDarkMode),
      });
      setIsLoadingDark(false);
    } catch (error) {
      dispatch({ type: '@@general/CHANGE_VIEW_MODE_ERROR' });
      setIsDarkMode(isDarkMode);
      setIsLoadingDark(false);
    }
  };

  const onLogoutClick = async (e) => {
    e.preventDefault();
    setLogoutLoading(true);
    setOpenAcc(false);
    try {
      await authLogout();
      onCloseWebsocket();
      dispatch(userLogoutCredentialsAction(null));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      Store.addNotification({
        title: 'Error',
        message: errorMessage,
        type: 'danger',
        insert: 'top',
        container: 'bottom-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 3000,
        },
      });
      setLogoutLoading(false);
    }
  };

  return (
    <>
      {isLogoutLoading ? <Loader text="Logout" styles={{ zIndex: 10000 }} /> : null}
      <Navbar fluid fixedTop expanded={expanded} onToggle={setExpanded}>
        <Navbar.Header>
          <button onClick={() => setExpanded(!expanded)} className={`navbar-toggler ${expanded ? 'open' : ''}`}>
            <span />
            <span />
            <span />
            <span />
          </button>
          <ul className="nav navbar-right" id="navbar-right" ref={rightNavbar}>
            {parsingCredits != null ? (
              <NavItem>
                <div className="nav__link nav__link-credits" onClick={(e) => e.preventDefault()}>
                  <Tooltip text="Parsing credits">
                    <Badge>{parsingCredits}</Badge>
                  </Tooltip>
                </div>
              </NavItem>
            ) : null}
            <NavItem>
              <Guides guidesFrameRef={guidesFrameRef} />
            </NavItem>
            <NavItem>
              <AdminNotification component={RouterLink} socketsInit={socketsInit} updateNoti={updateNoti} setUpdateNoti={setUpdateNoti} />
            </NavItem>
            <NavDropdown
              open={openAcc}
              onToggle={onToggleAcc}
              className="nav__item nav__item-account"
              pullRight
              title={<span className="fas fa-user-circle nav-icon" />}
              id="nav-dropdown-account"
              noCaret
            >
              <NavItem>
                <div className="nav__link nav__link-mode">
                  <span className="fas fa-moon nav-icon" />
                  {' '}
                  Dark mode
                  <Switch size="small" id="dark-mode" name="dark-mode" disabled={isLoadingDark} checked={isDarkMode} onChange={onChangeSwitch} />
                </div>
              </NavItem>
              {accountItem && (
                <NavItem link={accountItem.link} key={accountItem.id}>
                  <RouterLink
                    className="nav__link"
                    to={accountItem.link}
                    onClick={() => {
                      setExpanded(false);
                      onToggleAcc();
                    }}
                  >
                    <span className="fas fa-cog nav-icon" />
                    {accountItem?.name || ''}
                  </RouterLink>
                </NavItem>
              )}
              <>
                <li className="divider" />
                <MenuItem onClick={onLogoutClick}>
                  <span className="fas fa-sign-out nav-icon" />
                  Logout
                </MenuItem>
              </>
            </NavDropdown>
          </ul>
        </Navbar.Header>
        <Navbar.Collapse>
          <ul className="nav navbar-nav" id="navbar-left" ref={mainNavbar}>
            {navbar.priorityItems.map((el) => (
              <NavItem link={el.link} key={el.id}>
                <RouterLink className="nav__link" to={el.link} onClick={() => setExpanded(false)}>
                  {el.name ? el.name : ''}
                  {' '}
                  {el.count > 0 && <Badge className="badge-header">{el.count}</Badge>}
                </RouterLink>
              </NavItem>
            ))}
          </ul>

          {navbar.moreItems.length > 0 && (
            <ul className="nav navbar-nav">
              <NavDropdown
                open={openMore}
                onToggle={onToggleMore}
                className="nav__item-more"
                pullRight
                title={<span className="fa fa-ellipsis-v" aria-hidden="true" />}
                id="nav-dropdown"
                noCaret
              >
                {navbar.moreItems.map((el) => (
                  <NavItem link={el.link} key={el.id}>
                    <RouterLink
                      className="nav__link"
                      to={el.link}
                      onClick={() => {
                        onToggleMore(false);
                        setExpanded(false);
                      }}
                    >
                      {el.name ? el.name : ''}
                      {' '}
                      {el.count > 0 && <Badge className="badge-header">{el.count}</Badge>}
                    </RouterLink>
                  </NavItem>
                ))}
              </NavDropdown>
            </ul>
          )}
        </Navbar.Collapse>
      </Navbar>
    </>
  );
};

AdminNavbar.propTypes = {
  component: PropTypes.any,
  accessRoutes: PropTypes.array,
  defaultRoutes: PropTypes.array,
  parsingCredits: PropTypes.number,
  darkMode: PropTypes.number,
};

export default AdminNavbar;
